/**
 * module-access — pure engine для конструктора модульного доступа.
 * access-control (2026-06-22). Shared между BE (requireModule enforcement) и
 * FE (nav-gating + deep-link guard). Без side effects, без импортов db/llm.
 *
 * Семантика:
 *   moduleAccess(role, grants) = ROLE_DEFAULT_MODULES[role] ∪ explicit-allow ∖ explicit-deny
 *
 * Гранты — explicit override per (user, entity, module). Отсутствие override = дефолт роли.
 * `admin` / super-admin — всегда все модули (self-lockout protection; super-admin резолвится
 * выше, на уровне middleware/scope).
 *
 * Гейтинг во FE: `moduleAccess.has(item.module) && canAccess(role, routeKey)` —
 * модуль работает как дополнительный (сужающий) фильтр; основной сценарий — DENY
 * (закрыть модуль роли, у которой он есть по дефолту).
 */
import { ALL_MODULES, type Module } from '../enums.js';

// Re-export для потребителей движка (BE middleware, /auth/me), чтобы тянуть из одного места.
export { ALL_MODULES };
export type { Module };

/** Минимальная форма гранта, нужная движку (entity-scoped резолвится вызывающим). */
export type ModuleGrantInput = {
  module: Module;
  allowed: boolean;
};

/**
 * Дефолтный набор модулей per-роль. Выведен из текущих FE nav-RBAC правил
 * (lib/rbac.ts canAccess) — модуль входит в дефолт, если у роли есть ≥1 доступный
 * по canAccess роут в этом модуле. Это гарантирует отсутствие регрессии: при пустых
 * грантах поведение = текущее (модуль не добавляет доступа сверх canAccess).
 *
 * `admin` — все модули (см. resolveModuleAccess: жёсткий override).
 */
export const ROLE_DEFAULT_MODULES: Record<string, Module[]> = {
  admin: [...ALL_MODULES],
  company_admin: [
    'agent_review',
    'inventory',
    'production',
    'finance',
    'projects',
    'tasks',
    'reporting',
    'admin',
  ],
  // «Руководитель проектов» (2026-07-02): по умолчанию склад, производство, проекты
  // и задачи. Финансы и отчётность — вне дефолта роли (при необходимости выдаются
  // explicit-грантом per-user).
  //
  // Агент-ревью возвращён в дефолт 07.09.2026 (решение владельца). Под этим модулем
  // живёт `/api/v1/chat/*`, а чат с пака chat-interactive-actions — рабочий
  // инструмент задач именно руководителя проектов (фильтр, массовая правка,
  // черновики). Без модуля отправка получала 403 «Module access denied», и на проде
  // это выглядело как «мигнуло и сбросилось» (см. CIA-B10/CIA-F26 в книгах долгов).
  // На проде 07.09 всем 51 членствам роли выдан явный allow-грант — до выката
  // этого дефолта он и держит доступ; после выката строки избыточны, но безвредны.
  manager: ['agent_review', 'inventory', 'production', 'projects', 'tasks'],
  accountant: [
    'agent_review',
    'inventory',
    'production',
    'finance',
    'projects',
    'tasks',
    'reporting',
  ],
  reviewer: ['agent_review', 'inventory', 'finance', 'reporting'],
  sme: ['agent_review', 'inventory', 'finance', 'reporting'],
  production_planner: ['agent_review', 'inventory', 'production', 'reporting'],
  warehouse_operator: ['agent_review', 'inventory', 'reporting'],
  // inventory добавлен (2026-06-22 hardening): /items,/warehouses,/batches,/boms,/stock —
  // all-access на FE (canAccess undefined), значит PM-роли их видят и достигают (200).
  // Без inventory в дефолте mount-level requireModule('inventory') залочил бы их (регрессия).
  viewer: ['agent_review', 'inventory', 'finance', 'reporting'],
  /**
   * B1 (service-role §2) — ПУСТОЙ дефолт, и это ядро решения.
   *
   * Полярность переворачивается: у человека отсутствие строки гранта означает
   * «разрешено дефолтом роли», у сервиса — «не разрешено ничего». Забытая строка
   * ЗАКРЫВАЕТ доступ, а не открывает. До этого добавить сервису членство в новой
   * компании и не завести четыре строки значило дать ему финансы клиента и НЕ дать
   * производство, ради которого всё затевалось.
   *
   * Пустой массив здесь обязателен ещё и потому, что отсутствие ключа роняет роль
   * в фолбэк `['agent_review']` ниже — то есть молча выдаёт агент-ревью.
   */
  service: [],
};

/** Роли, которым нельзя залочить модуль `admin` (self-lockout protection). */
const ALWAYS_ALL_MODULES_ROLES = new Set(['admin']);

/**
 * Эффективный набор модулей для роли с учётом explicit-грантов.
 * @param role — effectiveRole из membership активной компании.
 * @param grants — explicit overrides (allow/deny) для (user, entity).
 */
export function resolveModuleAccess(role: string, grants: ModuleGrantInput[] = []): Set<Module> {
  if (ALWAYS_ALL_MODULES_ROLES.has(role)) return new Set(ALL_MODULES);

  const base = ROLE_DEFAULT_MODULES[role] ?? ['agent_review'];
  const result = new Set<Module>(base);

  for (const g of grants) {
    if (g.allowed) result.add(g.module);
    else result.delete(g.module);
  }

  // admin-модуль не может быть выдан не-admin/company_admin грантом (защита от эскалации).
  if (role !== 'company_admin' && !ALWAYS_ALL_MODULES_ROLES.has(role)) {
    const baseHasAdmin = base.includes('admin');
    if (!baseHasAdmin) result.delete('admin');
  }

  return result;
}

/** Удобный boolean-хелпер для middleware/guard. */
export function hasModuleAccess(
  role: string,
  module: Module,
  grants: ModuleGrantInput[] = [],
): boolean {
  return resolveModuleAccess(role, grants).has(module);
}

/**
 * Карта route → Module (SSOT, F0.2). Используется FE (тег nav-item) и для deep-link guard.
 * Префиксное сопоставление: самый длинный совпавший префикс выигрывает.
 */
export const ROUTE_MODULE_MAP: Array<{ prefix: string; module: Module }> = [
  // agent_review (+ workspaces)
  { prefix: '/runs', module: 'agent_review' },
  { prefix: '/conflicts', module: 'agent_review' },
  { prefix: '/anomalies', module: 'agent_review' },
  { prefix: '/evidence', module: 'agent_review' },
  { prefix: '/chat', module: 'agent_review' },
  { prefix: '/approvals', module: 'agent_review' },
  { prefix: '/reconciliation', module: 'agent_review' },
  { prefix: '/writeoff', module: 'agent_review' },
  { prefix: '/cost', module: 'agent_review' },
  { prefix: '/period-close', module: 'agent_review' },
  // inventory
  { prefix: '/items', module: 'inventory' },
  { prefix: '/warehouses', module: 'inventory' },
  { prefix: '/batches', module: 'inventory' },
  { prefix: '/boms', module: 'inventory' },
  { prefix: '/stock', module: 'inventory' },
  { prefix: '/stock-entries', module: 'inventory' },
  { prefix: '/stock-reconciliation', module: 'inventory' },
  { prefix: '/landed-cost', module: 'inventory' },
  // production
  { prefix: '/work-orders', module: 'production' },
  // finance
  { prefix: '/finance', module: 'finance' },
  { prefix: '/counterparties', module: 'finance' },
  { prefix: '/invoices', module: 'finance' },
  { prefix: '/payments', module: 'finance' },
  { prefix: '/settlements', module: 'finance' },
  { prefix: '/budgets', module: 'finance' },
  { prefix: '/sales-orders', module: 'finance' },
  // projects / tasks (живут в finance-секции, но отдельные модули доступа)
  { prefix: '/projects', module: 'projects' },
  { prefix: '/work-tasks', module: 'tasks' },
  { prefix: '/work-blueprints', module: 'tasks' },
  // reporting
  { prefix: '/excel', module: 'reporting' },
  { prefix: '/docs', module: 'reporting' },
  // admin
  { prefix: '/admin', module: 'admin' },
];

/**
 * Маршруты-исключения из module-гейтинга: общие мастер-данные, доступные всем
 * авторизованным независимо от module_access (продуктовое решение 2026-06-30).
 * Контрагенты — справочник клиентов/поставщиков, нужен finance, projects (клиент
 * проекта), sales. Тенантность всё равно через entity-scope/RLS.
 */
// `/me` — личные настройки пользователя (профиль, пароль, 2FA, язык, тема): self-service,
// не привязаны к бизнес-модулю. Без exempt moduleForRoute даёт fallback `agent_review`,
// и роль без него (напр. «Руководитель проектов») теряет доступ к собственным настройкам.
// `/notifications` — персональный центр уведомлений (notification-center I8, раунд 2):
// поверхность уровня `/me`, доступна каждому авторизованному независимо от module_access.
// BE-ручки уже под `requireAuth()` без module-гейта; без exempt fallback `agent_review`
// блокировал руководителя проектов (прод-жалоба 2026-07-19).
export const MODULE_EXEMPT_PREFIXES = ['/counterparties', '/me', '/notifications'];

export function isModuleExempt(pathname: string): boolean {
  return MODULE_EXEMPT_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Резолв модуля по pathname (для route-guard). `/` (dashboard) → agent_review. */
export function moduleForRoute(pathname: string): Module {
  let best: { prefix: string; module: Module } | null = null;
  for (const entry of ROUTE_MODULE_MAP) {
    if (pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  return best?.module ?? 'agent_review';
}
